import { compact, filter, map, uniqBy } from 'es-toolkit/compat';

import { ExperimentResult } from 'interfaces/experimentResults';
import { AttributesTable, slideResultToAttributesTable } from 'interfaces/inferenceArtifacts';
import { FeatureMetadata, heatmapUrlToPresignedGetUrl, heatmapUrlToPresignedHeadUrl } from './featureMetadata';
import { getPresentationInfoForOptionKey } from './parseHeatmaps';

export interface ParquetArtifactEntry {
  id: string;
  orchestrationId?: string;
  title: string;
  urlForGetRequest: string;
  urlForHeadRequest?: string;
  optionKey?: string;
  artifactUrl?: string;
}

type ArtifactHeatmap = FeatureMetadata & { optionKey?: string; artifactUrl?: string };

const toParquetArtifactEntry = (heatmap: ArtifactHeatmap, fallbackTitle: string): ParquetArtifactEntry | undefined => {
  if (!heatmap.heatmapUrl) return undefined;
  const urlForGetRequest = heatmapUrlToPresignedGetUrl(heatmap.heatmapUrl);
  if (!urlForGetRequest) return undefined;
  return {
    id: heatmap.orchestrationId || heatmap.id,
    orchestrationId: heatmap.orchestrationId,
    title: fallbackTitle,
    urlForGetRequest,
    urlForHeadRequest: heatmapUrlToPresignedHeadUrl(heatmap.heatmapUrl),
    ...(heatmap.optionKey ? { optionKey: heatmap.optionKey } : {}),
    ...(heatmap.artifactUrl ? { artifactUrl: heatmap.artifactUrl } : {}),
  };
};

export const getParquetArtifactEntries = (
  heatmaps: FeatureMetadata[],
  fallbackTitle: string,
  attributesTable?: AttributesTable
): ParquetArtifactEntry[] =>
  uniqBy(
    compact(
      map(
        filter(
          heatmaps,
          (heatmap) =>
            !attributesTable ||
            (Boolean(heatmap.resultType) && slideResultToAttributesTable[heatmap.resultType] === attributesTable)
        ),
        (heatmap) => toParquetArtifactEntry(heatmap, fallbackTitle)
      )
    ),
    'urlForGetRequest'
  );

export const getParquetArtifactEntriesFromExperimentResult = (
  experimentResult: ExperimentResult,
  fallbackTitle: string,
  attributesTable?: AttributesTable
): ParquetArtifactEntry[] =>
  getParquetArtifactEntries(
    compact(
      map(experimentResult.options, (option) => {
        const presentationInfo = getPresentationInfoForOptionKey(experimentResult, option.key);

        return presentationInfo && typeof presentationInfo !== 'string' && presentationInfo.file_type === 'parquet'
          ? ({
              id: `${experimentResult.experimentResultId}-${option.key}`,
              heatmapUrl: presentationInfo.storage_url,
              orchestrationId: experimentResult.orchestrationId,
              resultType: experimentResult.resultType,
              optionKey: `${option.key}`,
              artifactUrl: presentationInfo.artifact_url,
            } as ArtifactHeatmap)
          : undefined;
      })
    ),
    fallbackTitle,
    attributesTable
  );
